import { areaDoDesenho } from './caberNaArea';
import { PAD_BOTTOM, type EdgePosicionada, type GraphLayout } from './useGraphLayout';

/**
 * **Esticamento vertical** do mapa de acesso (29/08).
 *
 * `caberNaArea` leva o desenho a 90 % do eixo **limitante**; o outro fica onde a
 * proporção deixar, porque a escala é uniforme (tem de ser: `scale(1, f)` no
 * mundo transformaria os discos em elipses). Com quatro colunas de até quatro
 * linhas o desenho é ~2,2 : 1 contra uma seção ~1,5 : 1, então a largura enchia
 * e a altura parava em ~60 %.
 *
 * A saída é espalhar as **linhas**, não escalar o desenho: o `y` de cada nó é
 * multiplicado por um fator na hora de desenhar, e só ele — disco, ícone e os
 * dois rótulos continuam do tamanho que são, então nada distorce e nada se
 * sobrepõe (as linhas só se afastam). Escolhido o fator que faz a proporção do
 * desenho bater com a da seção, o enquadramento passa a encher os dois eixos.
 *
 * O fator **não** entra em `useGraphLayout`: o que fica gravado em
 * `localStorage` é a posição no espaço do layout, sem esticamento nenhum. Quem
 * arrasta um nó tem o inverso aplicado antes de gravar (`desesticar`), senão a
 * posição salva num monitor largo reapareceria deslocada num estreito — o fator
 * depende da seção, e a memória do usuário não pode depender dele.
 */

/** Teto do fator: mais do que isto e o mapa vira pontos soltos numa faixa. */
export const FATOR_MAX = 3;

export interface Esticamento {
  /** Multiplicador do `y` **no desenho**. 1 é «não há o que esticar». */
  fator: number;
  /**
   * O `y` que fica parado. É o topo da faixa de nós: esticar em torno dele
   * mantém a primeira linha logo abaixo dos cabeçalhos de coluna (que estão num
   * `y` fixo e não esticam) e espalha o resto para baixo. Em torno da origem, a
   * primeira linha desceria e abriria um vão sob os cabeçalhos.
   */
  ancora: number;
}

export const WITHOUT_ESTICAMENTO: Esticamento = { fator: 1, ancora: 0 };

/** Espaço do layout → espaço do desenho. */
export function esticar(y: number, { fator, ancora }: Esticamento): number {
  return ancora + (y - ancora) * fator;
}

/** Espaço do desenho → espaço do layout. O inverso exato de `esticar`. */
export function desesticar(y: number, { fator, ancora }: Esticamento): number {
  return ancora + (y - ancora) / fator;
}

export interface FaixaDeNos {
  ancora: number;
  /** Do primeiro ao último centro de disco — é só isto que estica. */
  extensao: number;
}

export function faixaDeNos(layout: GraphLayout): FaixaDeNos | null {
  if (layout.nos.length === 0) return null;
  const alturas = layout.nos.map((item) => item.y);
  const ancora = Math.min(...alturas);
  return { ancora, extensao: Math.max(...alturas) - ancora };
}

/**
 * Fator que faz a proporção do desenho bater com a da seção.
 *
 * A altura do desenho tem duas partes: a que estica (`extensao`, do primeiro ao
 * último centro de disco) e a que **não** (rótulos abaixo do último nó, anel do
 * primeiro, faixa dos cabeçalhos de coluna) — chame-a `fixa`. Então
 * `altura(f) = fixa + extensao × f`, e o alvo é a altura em que
 * `largura / altura = elLargura / elAltura`:
 *
 * ```
 * f = (largura × elAltura / elLargura − fixa) / extensao
 * ```
 *
 * Nunca abaixo de 1: um grafo já mais alto que a seção não se comprime — aí
 * quem limita é a altura, e o enquadramento sozinho resolve.
 *
 * O fator é uma heurística; o enquadramento é exato. Se a estimativa errar, o
 * desenho fica em 90 % de um eixo e um pouco menos no outro — nunca cortado.
 */
export function esticamentoFor(
  layout: GraphLayout,
  elemento: { largura: number; altura: number } | null,
): Esticamento {
  if (!elemento || !(elemento.largura > 0) || !(elemento.altura > 0)) return WITHOUT_ESTICAMENTO;

  const area = areaDoDesenho(layout);
  const faixa = faixaDeNos(layout);
  if (!area || !faixa || !(area.largura > 0) || !(faixa.extensao > 0)) return WITHOUT_ESTICAMENTO;

  const heightTarget = (area.largura * elemento.altura) / elemento.largura;
  const fixed = area.altura - faixa.extensao;
  const fator = (heightTarget - fixed) / faixa.extensao;
  if (!Number.isFinite(fator)) return WITHOUT_ESTICAMENTO;

  return { fator: Math.min(FATOR_MAX, Math.max(1, fator)), ancora: faixa.ancora };
}

/**
 * O mesmo layout com o `y` de cada nó no espaço do desenho.
 *
 * As arestas são reapontadas para os nós esticados (elas guardam a referência,
 * não uma cópia do ponto), e a moldura cresce junto — senão o `viewBox` ficaria
 * mais curto que o desenho e o `preserveAspectRatio` encolheria tudo de volta.
 */
export function esticarLayout(layout: GraphLayout, esticamento: Esticamento): GraphLayout {
  if (esticamento.fator === 1) return layout;

  const nos = layout.nos.map((item) => ({ ...item, y: esticar(item.y, esticamento) }));
  const byID = new Map(nos.map((item) => [item.no.id, item]));
  const arestas: EdgePosicionada[] = layout.arestas.map((item) => ({
    ...item,
    de: byID.get(item.aresta.from) ?? item.de,
    para: byID.get(item.aresta.to) ?? item.para,
  }));

  return {
    ...layout,
    nos,
    arestas,
    altura: Math.max(layout.altura, Math.max(...nos.map((item) => item.y)) + PAD_BOTTOM),
  };
}
